import { Edit2, Link, Plus, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useToast } from '../../context/ToastContext';
import collaborationService from '../../services/collaborationService';
import GiftOrderModal from '../GiftOrderModal';
import LoadingSpinner from '../ui/LoadingSpinner';
import Modal from '../ui/Modal';

const COLLAB_STATUS_OPTIONS = [
  { value: 'shortlisted', label: 'Shortlisted' },
  { value: 'outreach_pending', label: 'Outreach Pending' },
  { value: 'reached_out', label: 'Reached Out' },
  { value: 'no_response', label: 'No Response' },
  { value: 'responded', label: 'Responded' },
  { value: 'in_negotiation', label: 'In Negotiation' },
  { value: 'deal_closed', label: 'Deal Closed' },
  { value: 'brief_sent', label: 'Brief Sent' },
  { value: 'content_in_progress', label: 'Content In Progress' },
  { value: 'content_submitted', label: 'Content Submitted' },
  { value: 'revision_requested', label: 'Revision Requested' },
  { value: 'content_approved', label: 'Content Approved' },
  { value: 'content_live', label: 'Content Live' },
  { value: 'payment_pending', label: 'Payment Pending' },
  { value: 'payment_done', label: 'Payment Done' },
  { value: 'campaign_complete', label: 'Campaign Complete' },
  { value: 'declined', label: 'Declined' },
  { value: 'ghosted', label: 'Ghosted' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'blacklisted', label: 'Blacklisted' },
];

const CONTENT_TYPES = ['reel', 'post', 'story', 'youtube', 'tiktok', 'other'];
const CURRENCIES = ['USD', 'INR', 'GBP', 'AED', 'EUR', 'SGD'];
const METRIC_FIELDS = [
  'likes',
  'comments',
  'shares',
  'saves',
  'reach',
  'impressions',
  'followers_gained',
  'revenue_attributed',
];

const emptyContent = () => ({
  content_url: '',
  content_type: 'reel',
  platform: 'instagram',
  posted_at: '',
  likes: '',
  comments: '',
  shares: '',
  saves: '',
  reach: '',
  impressions: '',
  followers_gained: '',
  revenue_attributed: '',
  promo_code: '',
  utm_link: '',
});

const pieceToForm = (piece) => ({
  content_url: piece.content_url || '',
  content_type: piece.content_type || 'reel',
  platform: piece.platform || 'instagram',
  posted_at: piece.posted_at ? piece.posted_at.split('T')[0] : '',
  likes: piece.likes?.toString() || '',
  comments: piece.comments?.toString() || '',
  shares: piece.shares?.toString() || '',
  saves: piece.saves?.toString() || '',
  reach: piece.reach?.toString() || '',
  impressions: piece.impressions?.toString() || '',
  followers_gained: piece.followers_gained?.toString() || '',
  revenue_attributed: piece.revenue_attributed?.toString() || '',
  promo_code: piece.promo_code || '',
  utm_link: piece.utm_link || '',
});

const buildPayload = (form) => ({
  content_url: form.content_url || null,
  content_type: form.content_type,
  platform: form.platform,
  posted_at: form.posted_at || null,
  likes: form.likes ? parseInt(form.likes) : null,
  comments: form.comments ? parseInt(form.comments) : null,
  shares: form.shares ? parseInt(form.shares) : null,
  saves: form.saves ? parseInt(form.saves) : null,
  reach: form.reach ? parseInt(form.reach) : null,
  impressions: form.impressions ? parseInt(form.impressions) : null,
  followers_gained: form.followers_gained ? parseInt(form.followers_gained) : null,
  revenue_attributed: form.revenue_attributed ? parseFloat(form.revenue_attributed) : null,
  promo_code: form.promo_code || null,
  utm_link: form.utm_link || null,
});

export default function CollaborationModal({ influencer, collaboration, onClose, onSave }) {
  const { showToast } = useToast();
  const isEdit = !!collaboration;

  const [form, setForm] = useState({
    status: collaboration?.status || 'shortlisted',
    agreed_rate: collaboration?.agreed_rate || '',
    agreed_currency: collaboration?.agreed_currency || 'USD',
    notes: collaboration?.notes || '',
    deliverables: collaboration?.deliverables || [],
    content_due_date: collaboration?.content_due_date ? collaboration.content_due_date.split('T')[0] : '',
    follow_up_date: collaboration?.follow_up_date ? collaboration.follow_up_date.split('T')[0] : '',
  });

  const [contentPieces, setContentPieces] = useState([]);
  const [loadingContent, setLoadingContent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showContentForm, setShowContentForm] = useState(false);
  const [editingContentId, setEditingContentId] = useState(null); // null = adding new
  const [contentForm, setContentForm] = useState(emptyContent());
  const [showGiftModal, setShowGiftModal] = useState(false);

  useEffect(() => {
    if (isEdit && collaboration?.id) {
      setLoadingContent(true);
      collaborationService
        .getContent({ collaboration_id: collaboration.id })
        .then(setContentPieces)
        .catch(() => {})
        .finally(() => setLoadingContent(false));
    }
  }, [isEdit, collaboration?.id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        influencer_id: influencer.id,
        status: form.status,
        agreed_rate: form.agreed_rate ? parseFloat(form.agreed_rate) : null,
        agreed_currency: form.agreed_currency,
        notes: form.notes || null,
        deliverables:
          form.deliverables.filter((d) => d.trim()).length > 0 ? form.deliverables.filter((d) => d.trim()) : null,
        content_due_date: form.content_due_date || null,
        follow_up_date: form.follow_up_date || null,
      };
      let saved;
      if (isEdit) {
        saved = await collaborationService.updateCollaboration(collaboration.id, payload);
      } else {
        saved = await collaborationService.createCollaboration(payload);
      }
      showToast(isEdit ? 'Collaboration updated' : 'Collaboration created', 'success');
      onSave(saved);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const openAddForm = () => {
    setEditingContentId(null);
    setContentForm(emptyContent());
    setShowContentForm(true);
  };

  const openEditForm = (piece) => {
    setEditingContentId(piece.id);
    setContentForm(pieceToForm(piece));
    setShowContentForm(true);
  };

  const closeContentForm = () => {
    setShowContentForm(false);
    setEditingContentId(null);
    setContentForm(emptyContent());
  };

  const generateUtmLink = () => {
    const params = new URLSearchParams({
      utm_source: 'instagram',
      utm_medium: 'influencer',
      utm_campaign: influencer.ig_username || 'kaay',
      utm_content: contentForm.promo_code || contentForm.content_type,
    });
    setContentForm((prev) => ({ ...prev, utm_link: `https://kaay.co/?${params.toString()}` }));
  };

  const handleSaveContent = async () => {
    setSaving(true);
    try {
      const payload = buildPayload(contentForm);
      if (editingContentId) {
        const saved = await collaborationService.updateContent(editingContentId, payload);
        setContentPieces((prev) => prev.map((c) => (c.id === editingContentId ? saved : c)));
        showToast('Content updated', 'success');
      } else {
        const saved = await collaborationService.createContent({
          ...payload,
          collaboration_id: collaboration.id,
          influencer_id: influencer.id,
        });
        setContentPieces((prev) => [...prev, saved]);
        showToast('Content added', 'success');
      }
      closeContentForm();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteContent = async (id) => {
    try {
      await collaborationService.deleteContent(id);
      setContentPieces((prev) => prev.filter((c) => c.id !== id));
      showToast('Content removed', 'success');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  return (
    <Modal maxWidth="2xl" closeOnBackdrop={false} onClose={onClose} className="rounded-xl shadow-2xl flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center px-6 py-4 border-b flex-shrink-0">
        <div>
          <h2 className="text-lg font-semibold">
            {isEdit ? `Collaboration #${collaboration.cycle_number}` : 'New Collaboration'}
          </h2>
          <p className="text-sm text-muted-foreground">
            {influencer.name} · @{influencer.ig_username}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowGiftModal(true)}
            className="px-3 py-1.5 text-sm border border-input rounded-lg hover:bg-muted/50"
          >
            Send Gift
          </button>
          <button onClick={onClose} className="text-muted-foreground/70 hover:text-foreground">
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
        {/* Status + Rate */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Status</label>
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-input rounded-lg focus:ring-2 focus:ring-foreground"
            >
              {COLLAB_STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Agreed Rate</label>
            <div className="flex gap-2">
              <input
                type="number"
                placeholder="0"
                value={form.agreed_rate}
                onChange={(e) => setForm({ ...form, agreed_rate: e.target.value })}
                className="flex-1 px-3 py-2 text-sm border border-input rounded-lg focus:ring-2 focus:ring-foreground"
              />
              <select
                value={form.agreed_currency}
                onChange={(e) => setForm({ ...form, agreed_currency: e.target.value })}
                className="px-3 py-2 text-sm border border-input rounded-lg focus:ring-2 focus:ring-foreground"
              >
                {CURRENCIES.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Dates */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Content Due Date</label>
            <input
              type="date"
              value={form.content_due_date}
              onChange={(e) => setForm({ ...form, content_due_date: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-input rounded-lg focus:ring-2 focus:ring-foreground"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Follow-up Date</label>
            <input
              type="date"
              value={form.follow_up_date}
              onChange={(e) => setForm({ ...form, follow_up_date: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-input rounded-lg focus:ring-2 focus:ring-foreground"
            />
          </div>
        </div>

        {/* Deliverables */}
        <div>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
            <label className="block text-sm font-medium text-foreground/80">Deliverables</label>
            <button
              type="button"
              onClick={() => setForm({ ...form, deliverables: [...form.deliverables, ''] })}
              className="text-xs text-foreground hover:underline"
            >
              + Add
            </button>
          </div>
          {form.deliverables.length === 0 ? (
            <p className="text-xs text-muted-foreground/70 italic">
              No deliverables specified. Click + Add to list what's expected.
            </p>
          ) : (
            <div className="space-y-1.5">
              {form.deliverables.map((d, i) => (
                <div key={i} className="flex gap-2">
                  <input
                    type="text"
                    value={d}
                    onChange={(e) => {
                      const next = [...form.deliverables];
                      next[i] = e.target.value;
                      setForm({ ...form, deliverables: next });
                    }}
                    placeholder="e.g. 1 Reel (60s), 2 Stories with link"
                    className="flex-1 px-2 py-1.5 text-sm border border-input rounded-lg focus:ring-1 focus:ring-foreground"
                  />
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, deliverables: form.deliverables.filter((_, j) => j !== i) })}
                    className="text-muted-foreground/70 hover:text-red-500"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-foreground/80 mb-1">Notes</label>
          <textarea
            rows={3}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Deal terms, anything important..."
            className="w-full px-3 py-2 text-sm border border-input rounded-lg focus:ring-2 focus:ring-foreground resize-none"
          />
        </div>

        {/* Content Pieces — only shown when editing an existing collaboration */}
        {isEdit && (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <h3 className="text-sm font-semibold text-foreground/80 uppercase tracking-wider">Content Pieces</h3>
              {!showContentForm && (
                <button
                  onClick={openAddForm}
                  className="flex items-center gap-1 text-sm text-foreground hover:underline"
                >
                  <Plus size={14} /> Add Content
                </button>
              )}
            </div>

            {loadingContent ? (
              <div className="flex justify-center py-4">
                <LoadingSpinner size="sm" />
              </div>
            ) : (
              <div className="space-y-2">
                {contentPieces.map((piece) => (
                  <div key={piece.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg text-sm">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium capitalize">{piece.content_type}</span>
                        {piece.content_url && (
                          <a
                            href={piece.content_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 dark:text-blue-400 hover:underline text-xs"
                            onClick={(e) => e.stopPropagation()}
                          >
                            View ↗
                          </a>
                        )}
                        {piece.utm_link && (
                          <span className="text-xs text-muted-foreground/70 flex items-center gap-0.5">
                            <Link size={10} /> UTM
                          </span>
                        )}
                      </div>
                      <div className="text-muted-foreground text-xs mt-0.5">
                        {[
                          piece.reach && `${piece.reach.toLocaleString()} reach`,
                          piece.likes && `${piece.likes.toLocaleString()} likes`,
                          piece.revenue_attributed && `$${piece.revenue_attributed} revenue`,
                          piece.promo_code && `Code: ${piece.promo_code}`,
                        ]
                          .filter(Boolean)
                          .join(' · ') || 'No metrics yet'}
                      </div>
                    </div>
                    <div className="flex gap-1 ml-2 flex-shrink-0">
                      <button
                        onClick={() => openEditForm(piece)}
                        className="p-1 text-muted-foreground/70 hover:text-foreground transition"
                        title="Edit"
                      >
                        <Edit2 size={13} />
                      </button>
                      <button
                        onClick={() => handleDeleteContent(piece.id)}
                        className="p-1 text-muted-foreground/70 hover:text-red-500 transition"
                        title="Delete"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                ))}
                {contentPieces.length === 0 && !showContentForm && (
                  <p className="text-sm text-muted-foreground/70 text-center py-3 border border-dashed rounded-lg">
                    No content pieces yet
                  </p>
                )}
              </div>
            )}

            {/* Add / Edit Content Form */}
            {showContentForm && (
              <div className="mt-3 p-4 border border-border rounded-lg space-y-3 bg-muted/50">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {editingContentId ? 'Edit Content Piece' : 'Add Content Piece'}
                </p>

                {/* URL + Type + Date */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  <div className="col-span-1">
                    <label className="block text-xs text-muted-foreground mb-1">Content URL</label>
                    <input
                      type="url"
                      value={contentForm.content_url}
                      onChange={(e) => setContentForm({ ...contentForm, content_url: e.target.value })}
                      placeholder="https://instagram.com/p/..."
                      className="w-full px-2 py-1.5 text-sm border border-input rounded-lg focus:ring-1 focus:ring-foreground"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">Type</label>
                    <select
                      value={contentForm.content_type}
                      onChange={(e) => setContentForm({ ...contentForm, content_type: e.target.value })}
                      className="w-full px-2 py-1.5 text-sm border border-input rounded-lg focus:ring-1 focus:ring-foreground"
                    >
                      {CONTENT_TYPES.map((t) => (
                        <option key={t} className="capitalize">
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">Posted At</label>
                    <input
                      type="date"
                      value={contentForm.posted_at}
                      onChange={(e) => setContentForm({ ...contentForm, posted_at: e.target.value })}
                      className="w-full px-2 py-1.5 text-sm border border-input rounded-lg focus:ring-1 focus:ring-foreground"
                    />
                  </div>
                </div>

                {/* Metrics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {METRIC_FIELDS.map((field) => (
                    <div key={field}>
                      <label className="block text-xs text-muted-foreground mb-1 capitalize">
                        {field.replace(/_/g, ' ')}
                      </label>
                      <input
                        type="number"
                        value={contentForm[field]}
                        onChange={(e) => setContentForm({ ...contentForm, [field]: e.target.value })}
                        className="w-full px-2 py-1.5 text-sm border border-input rounded-lg focus:ring-1 focus:ring-foreground"
                      />
                    </div>
                  ))}
                </div>

                {/* Promo Code + UTM */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">Promo Code</label>
                    <input
                      type="text"
                      value={contentForm.promo_code}
                      onChange={(e) => setContentForm({ ...contentForm, promo_code: e.target.value })}
                      placeholder="KAAY10"
                      className="w-full px-2 py-1.5 text-sm border border-input rounded-lg focus:ring-1 focus:ring-foreground"
                    />
                  </div>
                  <div>
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
                      <label className="block text-xs text-muted-foreground">UTM Link</label>
                      <button
                        type="button"
                        onClick={generateUtmLink}
                        className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        Generate
                      </button>
                    </div>
                    <input
                      type="url"
                      value={contentForm.utm_link}
                      onChange={(e) => setContentForm({ ...contentForm, utm_link: e.target.value })}
                      placeholder="https://kaay.co/?utm_..."
                      className="w-full px-2 py-1.5 text-sm border border-input rounded-lg focus:ring-1 focus:ring-foreground"
                    />
                  </div>
                </div>

                <div className="flex gap-2 justify-end">
                  <button
                    onClick={closeContentForm}
                    className="px-3 py-1.5 text-sm border border-input rounded-lg hover:bg-muted/50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveContent}
                    disabled={saving}
                    className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
                  >
                    {saving ? 'Saving…' : editingContentId ? 'Save Changes' : 'Add'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t flex gap-3 justify-end flex-shrink-0">
        <button onClick={onClose} className="px-4 py-2 text-sm border border-input rounded-lg hover:bg-muted/50">
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
        >
          {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Collaboration'}
        </button>
      </div>

      {showGiftModal && <GiftOrderModal influencer={influencer} onClose={() => setShowGiftModal(false)} />}
    </Modal>
  );
}
